import axios from "axios";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toastr from "../../utils/toast";
import Item from "./components/Notification/Item";
import { MaterialReactTable } from "material-react-table";
import Leave from "./components/Notification/Leave";
import Reimbursement from "./components/Notification/Reimbursement";

// ─── ADDED: reimbursement notification helpers ───────────────────────────────
//   No notifications table exists, so "read" state is remembered per-browser in
//   localStorage. The key includes the STATUS, so if a bill later moves
//   (e.g. hold -> paid) the new outcome shows up as unread again.
//     key format: "<reimbursement_id>:<status>"   e.g. "12:paid"
const REIMB_READ_KEYS = "reimb_notif_read_keys";

function getReimbReadKeys() {
  try {
    return JSON.parse(localStorage.getItem(REIMB_READ_KEYS) || "[]");
  } catch {
    return [];
  }
}

function saveReimbReadKeys(keys) {
  localStorage.setItem(REIMB_READ_KEYS, JSON.stringify(Array.from(new Set(keys))));
}

function reimbSubjectOf(rec) {
  if (rec?.subject) return rec.subject;
  if (rec?.travel_from || rec?.travel_to) {
    const loc = rec.travel_location ? ` (${rec.travel_location})` : "";
    return `${rec.travel_from || ""} \u2192 ${rec.travel_to || ""}${loc}`;
  }
  return "your reimbursement";
}

function buildReimbEvent(rec) {
  const map = {
    paid: {
      title: "Reimbursement Paid",
      tone: "success",
      text: "has been paid by the finance team",
    },
    "not-paid": {
      title: "Reimbursement Marked Not Paid",
      tone: "danger",
      text: "was marked as not paid by the finance team",
    },
    hold: {
      title: "Reimbursement On Hold",
      tone: "warning",
      text: "has been put on hold by the finance team",
    },
  };
  const cfg = map[rec.status];
  if (!cfg) return null;
  return {
    key: `${rec.id}:${rec.status}`,
    reimbursementId: rec.id,
    title: cfg.title,
    tone: cfg.tone,
    detail: cfg.text,
    rec,
  };
}

function Notifications() {
  const user = JSON.parse(localStorage.getItem("user"));
  const { token } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [approval2List, setApproval2List] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [openItemModal, setOpenItemModal] = useState(false);
  const [LeavesList, setLeavesList] = useState([]);
  const [SelectedUserLeave, setSelectedUserLeave] = useState({
    user_id: null,
    leave_id: null,
    user_name: null,
    leave_status: null,
    comments: null,
    no_of_days: null,
    from_date: null,
    to_date: null,
    reason: null,
  });
  const [openLeaveModal, setOpenLeaveModal] = useState(false);

  const [reimbursementList, setReimbursementList] = useState([]);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [openReimbursementModal, setOpenReimbursementModal] = useState(false);

  // ── ADDED: my reimbursement updates (finance status outcome) ──────────────
  const [reimbEvents, setReimbEvents] = useState([]);
  const unreadReimbCount = reimbEvents.filter((e) => !e._read).length;

  async function get_approval2_list() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/approval2/approval2_list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response?.data?.length > 0) {
        setApproval2List(response?.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function itemGet(invoice_has_item_id, invoice_id) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/approval2/item_get/${invoice_has_item_id}/${invoice_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data) {
        setItemData(response?.data);
        setOpenItemModal(true);
      }
    } catch (error) {
      console.log(error);
      toastr.error(error);
    }
  }

  async function get_leave_list() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/approvalleaves/approvalleaves_list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const leave = response?.data?.leaves;
      console.log(leave);
      if (leave?.length > 0) {
        setLeavesList(response?.data?.leaves);
      }
    } catch (error) {
      console.log(error);
    }
  }

  // ── ADDED: fetch my reimbursement updates ─────────────────────────────────
  async function get_my_reimbursement_notifications() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/reimbursements/notifications/mine`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const rows = response?.data?.data || [];
      const readKeys = getReimbReadKeys();
      setReimbEvents(
        rows
          .map((r) => buildReimbEvent(r))
          .filter(Boolean)
          .map((e) => ({ ...e, _read: readKeys.includes(e.key) }))
          .sort((a, b) => b.reimbursementId - a.reimbursementId),
      );
    } catch (error) {
      console.log(error);
    }
  }

  async function get_reimbursement_list() {
      try {
          const response = await axios.get(
              `${API_BASE_URL}/reimbursements/pending-payables?status=pending`,
              {
                  headers: {
                      Authorization: `Bearer ${token}`,
                  },
              }
          );

          setReimbursementList(response.data.data || []);
      } catch (err) {
          console.log(err);
      }
  }

  // ── ADDED: mark one / all as read (localStorage only) ─────────────────────
  function markReimbRead(key, all = false) {
    if (all) {
      saveReimbReadKeys([...getReimbReadKeys(), ...reimbEvents.map((e) => e.key)]);
      setReimbEvents((prev) => prev.map((e) => ({ ...e, _read: true })));
    } else {
      saveReimbReadKeys([...getReimbReadKeys(), key]);
      setReimbEvents((prev) =>
        prev.map((e) => (e.key === key ? { ...e, _read: true } : e)),
      );
    }
  }

  const invoice_approval_columns = [
    {
      accessorKey: "invoice_id",
      header: "Action",
      size: 30,
      Cell: ({ cell }) => {
        const invoice_has_item_id = cell.row.original.invoice_has_item_id;
        const invoice_id = cell.row.original.invoice_id;
        return (
          <button
            className="btn btn-sm btn-info"
            title="Approval"
            onClick={() => {
              itemGet(invoice_has_item_id, invoice_id);
            }}
          >
            <i className="fa-solid fa-thumbs-up"></i>
          </button>
        );
      },
    },
    {
      accessorKey: "order_id",
      header: "Order ID",
      size: 30,
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 130,
    },
    {
      accessorKey: "company_name",
      header: "Client",
    },
    {
      accessorKey: "role_name",
      header: "Role",
    },
    {
      accessorKey: "entity_name",
      header: "Entity",
    },
    {
      accessorKey: "payment_term",
      header: "Payment Term",
      size: 40,
    },
    {
      accessorKey: "raised_by",
      header: "Raised By",
      size: 130,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      Cell: ({ cell }) => {
        const symbol = cell.row.original.symbol;
        const amount = cell.row.original.amount;
        return (
          <>
            {symbol} {Number(amount).toLocaleString("en-IN")}
          </>
        );
      },
    },

  ];

  const leaves_columns = [
    {
      accessorKey: "id",
      header: "Action",
      size: 50,
      Cell: ({ cell }) => {
        const id = cell.row.original.id;
        return (
          <button
            className="btn btn-sm btn-info"
            title="Approval"
            onClick={() => {
              setSelectedUserLeave({
                user_id: cell.row.original.user_id,
                leave_id: id,
                user_name: cell.row.original.Emp_name,
                leave_status: cell.row.original.leave_status,
                comments: cell.row.original.comments,
                no_of_days: cell.row.original.num_leave_days,
                from_date: cell.row.original.date_from,
                to_date: cell.row.original.date_to,
                reason: cell.row.original.reason,
              });
              setOpenLeaveModal(true);
            }}
          >
            <i className="fa-solid fa-thumbs-up"></i>
          </button>
        );
      },
    },
    {
      accessorKey: "Emp_name",
      header: "Name",
      size: 130,
    },
    {
      accessorKey: "reporting_to",
      header: "Reporting To",
      size: 120,
    },
    {
      accessorKey: "leavetype",
      header: "Leave Type",
      size: 50,
    },
    {
      accessorKey: "apply_date",
      header: "Apply Date",
      size: 50,
    },
    {
      accessorKey: "date_from",
      header: "Date From",
      size: 50,
    },
    {
      accessorKey: "date_to",
      header: "Date To",
      size: 50,
    },
    {
      accessorKey: "num_leave_days",
      header: "Days",
      size: 30,
    },
    {
      accessorKey: "leave_status",
      header: "Status",
      size: 80,
    },
  ];

  const reimbursement_columns = [
      {
          accessorKey: "id",
          header: "Action",
          Cell: ({ row }) => (
              <button
                  className="btn btn-sm btn-info"
                  onClick={() => {
                      setSelectedReimbursement(row.original);
                      setOpenReimbursementModal(true);
                  }}
              >
                  <i className="fa fa-eye"></i>
              </button>
          ),
      },
      {
          accessorKey: "employee_name",
          header: "Employee",
      },
      {
          accessorKey: "expense_name",
          header: "Expense",
      },
      {
          accessorKey: "final_amount",
          header: "Amount",
          Cell: ({ row }) =>
              `${row.original.currency_symbol} ${row.original.final_amount}`,
      },
      {
          accessorKey: "authorised_by_name",
          header: "Authorised By",
      },
      {
          accessorKey: "status",
          header: "Status",
      },
      {
          accessorKey: "authorised",
          header: "Approval",
      },
  ];

  useEffect(() => {
    get_approval2_list();
    get_leave_list();
    get_reimbursement_list();
    get_my_reimbursement_notifications();
  }, [token]);

  return (
    <>
      {openItemModal && itemData && (
        <Item
          onClose={() => {
            setOpenItemModal(false);
            setItemData([]);
          }}
          data={itemData}
        />
      )}

      {Object.keys(SelectedUserLeave).length > 0 && openLeaveModal && (
        <Leave
          onClose={(s) => {
            setOpenLeaveModal(false);
            setSelectedUserLeave({});
            if (s === 1) {
              setLeavesList(
                LeavesList.filter(
                  (leave) => leave.id !== SelectedUserLeave.leave_id,
                ),
              );
            }
          }}
          data={SelectedUserLeave}
        />
      )}

      {openReimbursementModal && selectedReimbursement && (
          <Reimbursement
              data={selectedReimbursement}
              onClose={(updated) => {
                  setOpenReimbursementModal(false);
                  setSelectedReimbursement(null);

                  if (updated) {
                      get_reimbursement_list();
                  }
              }}
          />
      )}

      <div className="content">
        <div className="page-header">
          <div className="row">
            <div className="col">
              <h3 className="page-title">Notifications</h3>
            </div>
          </div>
        </div>

        <div className="row">
          {/* ── ADDED: My Reimbursement Updates ──────────────────────────── */}
          <div className="col-12 mb-2">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                  My Reimbursement Updates{" "}
                  {unreadReimbCount > 0 && (
                    <span className="badge bg-danger">{unreadReimbCount} new</span>
                  )}
                </h5>
                {unreadReimbCount > 0 && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => markReimbRead(null, true)}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="card-body p-0">
                {reimbEvents.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No reimbursement updates
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {reimbEvents.map((e) => (
                      <li
                        key={e.key}
                        className={`list-group-item d-flex justify-content-between align-items-start ${
                          e._read ? "" : "bg-light"
                        }`}
                      >
                        <div className="me-3">
                          <div className="fw-semibold">
                            {!e._read && (
                              <span className="badge bg-primary me-2">New</span>
                            )}
                            <span className={`text-${e.tone}`}>{e.title}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                              "{reimbSubjectOf(e.rec)}" — {e.rec.currency_symbol || ""}
                              {e.rec.final_amount || 0} ({e.rec.expense_name || "—"}) {e.detail}
                              {e.rec.remarks ? ` — "${e.rec.remarks}"` : ""}
                            </div>
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {e.rec.approved_date
                              ? new Date(e.rec.approved_date).toLocaleDateString(
                                  "en-GB",
                                )
                              : ""}
                          </div>
                        </div>
                        {!e._read && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => markReimbRead(e.key)}
                          >
                            Mark read
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="row">
                {(![6, 7].includes(user?.designation_id) ||
                  user?.admin === "1") && (
                  <>
                    {(user?.access.split(",").includes("29") || user?.admin === "1") && (
                      <div className="col-md-12 col-lg-12 col-xl-12 col-xxl-12 mb-2">
                        <div className="accordion customized-accordion accordions-items-seperate">
                          <div className="accordion-item custom-accordion-info">
                            <h2 className="accordion-header" id="customizedAccordionOne">
                              <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#customized-AccordionOne" aria-expanded="false" aria-controls="customized-AccordionOne">
                                2nd Approval (<span className="text-black">{approval2List?.length} Records</span>)
                              </button>
                            </h2>
                            <div id="customized-AccordionOne" className="accordion-collapse collapse" aria-labelledby="customizedAccordionOne" data-bs-parent="#customizedAccordion">
                              <div className="accordion-body">
                                <div className="table-responsive">
                                  {approval2List?.length > 0 ? (
                                    <MaterialReactTable
                                      columns={invoice_approval_columns}
                                      data={approval2List}
                                      enableStickyHeader
                                      enableStickyFooter
                                      initialState={{
                                        pagination: {
                                          pageIndex: 0,
                                          pageSize: 10,
                                        },
                                      }}
                                    />
                                  ) : (
                                    <div className="text-center">
                                      No Records Found
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {(user?.designation_id <= 4 && user?.admin === "1") || (
                      <div className="col-md-12 col-lg-12 col-xl-12 col-xxl-12 mb-2">
                        <div className="accordion customized-accordion accordions-items-seperate">
                          <div className="accordion-item custom-accordion-info">
                            <h2 className="accordion-header" id="customizedAccordionOne4">
                              <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#customized-AccordionOne4" aria-expanded="false" aria-controls="customized-AccordionOne4">
                                Leaves Approval (<span className="text-black">{LeavesList?.length} Records</span>)
                              </button>
                            </h2>
                            <div id="customized-AccordionOne4" className="accordion-collapse collapse" aria-labelledby="customizedAccordionOne4" data-bs-parent="#customizedAccordion">
                              <div className="accordion-body">
                                {LeavesList.length > 0 ? (
                                  <MaterialReactTable
                                    columns={leaves_columns}
                                    data={LeavesList}
                                    enableStickyHeader
                                    enableStickyFooter
                                    initialState={{
                                      pagination: {
                                        pageIndex: 0,
                                        pageSize: 10,
                                      },
                                    }}
                                  />
                                ) : (
                                  <div className="text-center">
                                    No Records Found
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {(user?.admin === "1" || user?.designation_id <= 3) && (
                  <div className="col-md-12 col-lg-12 col-xl-12 col-xxl-12 mb-2">
                    <div className="accordion customized-accordion accordions-items-seperate">
                      <div className="accordion-item custom-accordion-info">
                        <h2 className="accordion-header" id="customizedAccordionOne3">
                          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#customized-AccordionOne3" aria-expanded="false" aria-controls="customized-AccordionOne3">
                            Reimbursement Approval (<span className="text-black">{reimbursementList?.length} Records</span>)
                          </button>
                        </h2>
                        <div id="customized-AccordionOne3" className="accordion-collapse collapse" aria-labelledby="customizedAccordionOne3" data-bs-parent="#customizedAccordion">
                          <div className="accordion-body">
                            <MaterialReactTable
                                columns={reimbursement_columns}
                                data={reimbursementList}
                                enableStickyHeader
                                enableStickyFooter
                                initialState={{
                                    pagination: {
                                        pageIndex: 0,
                                        pageSize: 10,
                                    },
                                }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* {(user?.admin === "1" || user?.designation_id <= 4) && (
                  <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-3 mb-2">
                    <div className="accordion customized-accordion accordions-items-seperate">
                      <div className="accordion-item custom-accordion-info">
                        <h2
                          className="accordion-header"
                          id="customizedAccordionOne1"
                        >
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#customized-AccordionOne1"
                            aria-expanded="false"
                            aria-controls="customized-AccordionOne1"
                          >
                            Invoice Raised For Approval Till Today
                          </button>
                        </h2>
                        <div
                          id="customized-AccordionOne1"
                          className="accordion-collapse collapse"
                          aria-labelledby="customizedAccordionOne1"
                          data-bs-parent="#customizedAccordion"
                        >
                          <div className="accordion-body">
                            <strong>
                              This is the first item's accordion body.
                            </strong>{" "}
                            It is shown by default, until the collapse plugin
                            adds the appropriate classes that we use to style
                            each element. These classes control the overall
                            appearance, as well as the showing and hiding via
                            CSS transitions. You can modify any of this with
                            custom CSS or overriding our default variables. It's
                            also worth noting that just about any HTML can go
                            within the
                            <code>.accordion-body</code>, though the transition
                            does limit overflow.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {(user?.admin === "1" || user?.designation === 1) && (
                  <>
                    <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-3 mb-2">
                      <div className="accordion customized-accordion accordions-items-seperate">
                        <div className="accordion-item custom-accordion-info">
                          <h2
                            className="accordion-header"
                            id="customizedAccordionOne2"
                          >
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#customized-AccordionOne2"
                              aria-expanded="false"
                              aria-controls="customized-AccordionOne2"
                            >
                              Role Approval Status
                            </button>
                          </h2>
                          <div
                            id="customized-AccordionOne2"
                            className="accordion-collapse collapse"
                            aria-labelledby="customizedAccordionOne2"
                            data-bs-parent="#customizedAccordion"
                          >
                            <div className="accordion-body">
                              <strong>
                                This is the first item's accordion body.
                              </strong>{" "}
                              It is shown by default, until the collapse plugin
                              adds the appropriate classes that we use to style
                              each element. These classes control the overall
                              appearance, as well as the showing and hiding via
                              CSS transitions. You can modify any of this with
                              custom CSS or overriding our default variables.
                              It's also worth noting that just about any HTML
                              can go within the
                              <code>.accordion-body</code>, though the
                              transition does limit overflow.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                 */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Notifications;